import { installBuildApk } from '@/api/builds';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { IBuild } from '@/entities/build.entity';
import { SendIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface BuildInstallButtonProps {
	build: IBuild;
	variant?: 'encrypt' | 'raw';
	label?: string;
	className?: string;
	onInstalled?: () => void;
}

function canInstallVariant(build: IBuild, variant?: 'encrypt' | 'raw') {
	if (!build.successBuilded) return false;
	if (variant === 'encrypt') return Boolean(build.hasEncryptApk);
	if (variant === 'raw') return Boolean(build.hasRawApk);
	return Boolean(build.hasApk);
}

export default function BuildInstallButton({
	build,
	variant,
	label,
	className,
	onInstalled,
}: BuildInstallButtonProps) {
	const [loading, setLoading] = useState(false);
	const canInstall = canInstallVariant(build, variant);
	const isUnavailable = Boolean(build.successBuilded && !canInstall);
	const buttonLabel = label ?? (isUnavailable ? 'APK недоступен' : 'Установить');

	async function handleInstall() {
		if (!canInstall) return;

		setLoading(true);
		try {
			await installBuildApk(build.id, variant);
			onInstalled?.();
			toast.success('Архив отправлен в Telegram', { position: 'top-center', duration: 2500 });
		} catch {
			// error toast handled by api interceptor
		} finally {
			setLoading(false);
		}
	}

	if (!canInstall && !isUnavailable) return null;

	return (
		<Button
			variant='outline'
			size='sm'
			className={className}
			disabled={loading || isUnavailable}
			onClick={handleInstall}
			title={isUnavailable ? 'Файл отсутствует, пересоберите билд' : undefined}
		>
			{loading ? <Spinner className='size-4' /> : <SendIcon size={16} />}
			{buttonLabel}
		</Button>
	);
}
